// Copyright (c) Microsoft Corporation.
// Licensed under the MIT license.

import { useState } from 'react';
import { ITheme, mergeStyles, useTheme } from '@fluentui/react';
import { Mic28Filled, MicOff28Regular } from '@fluentui/react-icons';
import { useSpeechVolume } from 'utils/subscribeToSpeechVolume';

export type PushToTalkButtonState = 'startingTalk' | 'talking' | 'stoppingTalk' | 'stopped';

export const PushToTalkButton = (props: {
  onStateChange: (state: PushToTalkButtonState) => void;
  talkOn: () => Promise<void>;
  talkOff: () => Promise<void>;
}): JSX.Element => {
  const [talkState, setTalkState] = useState<PushToTalkButtonState>('stopped');

  const setTalkStateAndNotify = (state: PushToTalkButtonState): void => {
    setTalkState(state);
    props.onStateChange(state);
  };

  const startTalk = async (): Promise<void> => {
    try {
      setTalkStateAndNotify('startingTalk');
      await props.talkOn();
      setTalkStateAndNotify('talking');
    } catch {
      setTalkStateAndNotify('stopped');
    }
  };

  const stopTalk = async (): Promise<void> => {
    try {
      setTalkStateAndNotify('stoppingTalk');
      await props.talkOff();
      setTalkStateAndNotify('stopped');
    } catch {
      setTalkStateAndNotify('talking');
    }
  };

  const theme = useTheme();
  const icon = talkState === 'talking' || talkState === 'startingTalk' ? <Mic28Filled /> : <MicOff28Regular />;
  const buttonDisabled = talkState === 'stoppingTalk' || talkState === 'startingTalk';
  const speakingVolume = useSpeechVolume(talkState === 'talking');
  const click = talkState === 'stopped' ? startTalk : stopTalk;

  return (
    <div style={{ position: 'relative' }}>
      <div
        className={buttonStyle({ theme, disabled: buttonDisabled, volume: speakingVolume })}
        onClick={click}
        onKeyDown={(e) => {
          if ((e.key === 'Enter' || e.key === ' ') && !buttonDisabled) {
            click();
          }
        }}
        tabIndex={0}
        role="button"
        aria-disabled={buttonDisabled}
      >
        {icon}
      </div>
    </div>
  );
};

const buttonStyle = (args: {
  theme: ITheme;
  disabled: boolean;
  /** Between 0-100 */
  volume?: number;
}): string => {
  const { theme, volume, disabled } = args;

  const backgroundColor = disabled ? theme.palette.neutralTertiaryAlt : theme.palette.themeLight;
  const backgroundColorHover = disabled ? theme.palette.neutralTertiaryAlt : theme.palette.themeLighter;
  const backgroundColorActive = disabled ? theme.palette.neutralTertiaryAlt : theme.palette.themeLight;

  return mergeStyles({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    backgroundColor: backgroundColor,
    color: theme.palette.themePrimary,
    borderColor: 'transparent',
    borderWidth: '0.25rem',
    borderRadius: '100%',
    height: '6rem',
    width: '6rem',
    cursor: 'pointer',
    ':hover': {
      backgroundColor: backgroundColorHover
    },
    ':active': {
      backgroundColor: backgroundColorActive
    },
    ':focus': {
      borderColor: theme.semanticColors.focusBorder
    },

    // Psuedo-element to create the glowing background as the user speaks
    '::before': {
      content: '""',
      background: `radial-gradient(circle, ${theme.palette.themeSecondary}, transparent 70%)`,
      filter: 'blur(0.25rem)',
      height: `${volume}%`,
      width: `${volume}%`,
      borderRadius: '100%',
      position: 'absolute',
      zIndex: 0
    },

    // Position content above the ::before pseudo-element
    '> *': {
      position: 'absolute',
      zIndex: 1
    }
  });
};
